import {
  SaveTrustAssetCommand,
  SetTrustAssetStatusCommand,
  TrustAssetDetailDto,
  TrustAssetDto,
  PublicTrustFaqEntryDto,
  ReturnTrustFaqEntryCommand,
  SaveTrustFaqEntryCommand,
  TrustFaqBatchResultDto,
  TrustFaqEntryDto,
  ApproveTrustAccessRequestCommand,
  CreateTrustAccessRequestCommand,
  CreateTrustCustomerDomainCommand,
  DeclineTrustAccessRequestCommand,
  TrustAccessGrantDto,
  TrustAccessRequestDto,
  TrustAccessRequestStatus,
  TrustAccessSessionDto,
  TrustCustomerDomainDto,
  TrustRestrictedDocumentDto,
  CreateTrustPolicyCommand,
  DecideTrustPolicyChangeCommand,
  ProposeTrustPolicyChangesCommand,
  PublicTrustOverviewDto,
  PublicTrustNdaDto,
  PublicTrustPolicyDto,
  SaveTrustSubprocessorCommand,
  TrustFactsDto,
  TrustSubprocessorDto,
  UpdateTrustFactsCommand,
  ReturnTrustPolicyVersionCommand,
  SaveTrustPolicyDraftCommand,
  SubmitTrustPolicyVersionCommand,
  TrustAuditEventDto,
  TrustPersonDto,
  TrustPolicyDetailDto,
  TrustPolicySummaryDto,
  TrustPolicyVersionDto,
  UpdateTrustPolicyCommand,
} from "@coaching-culture/types";
import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "react-query";

// Trust Centre policy admin: super-partner staff in the partners portal.

const admin = "/api/v2/trust/admin";

const keys = {
  all: ["trust"] as const,
  people: ["trust", "people"] as const,
  policies: ["trust", "policies"] as const,
  policy: (id: string) => ["trust", "policies", id] as const,
  version: (id: string) => ["trust", "versions", id] as const,
  audit: (policyId: string | null) => ["trust", "audit", policyId] as const,
};

export const useTrustPeople = () =>
  useQuery(keys.people, () =>
    axios.get<TrustPersonDto[]>(`${admin}/people`).then(({ data }) => data),
  );

export const useTrustPolicies = () =>
  useQuery(keys.policies, () =>
    axios
      .get<TrustPolicySummaryDto[]>(`${admin}/policies`)
      .then(({ data }) => data),
  );

export const useTrustPolicy = (policyId: string | null) =>
  useQuery(
    keys.policy(policyId ?? ""),
    () =>
      axios
        .get<TrustPolicyDetailDto>(`${admin}/policies/${policyId}`)
        .then(({ data }) => data),
    { enabled: !!policyId },
  );

export const useTrustPolicyVersion = (versionId: string | null) =>
  useQuery(
    keys.version(versionId ?? ""),
    () =>
      axios
        .get<TrustPolicyVersionDto>(`${admin}/versions/${versionId}`)
        .then(({ data }) => data),
    { enabled: !!versionId },
  );

export const useTrustAudit = (policyId: string | null = null) =>
  useQuery(keys.audit(policyId), () =>
    axios
      .get<TrustAuditEventDto[]>(`${admin}/audit`, {
        params: policyId ? { policyId } : {},
      })
      .then(({ data }) => data),
  );

/**
 * Every write returns the updated resource. Put versions straight into the
 * cache and refresh the policy list, details and audit log.
 */
const useTrustMutation = <TArgs>(
  fn: (args: TArgs) => Promise<TrustPolicyVersionDto>,
) => {
  const qc = useQueryClient();
  return useMutation(fn, {
    onSuccess: (version) => {
      qc.setQueryData(keys.version(version.id), version);
      qc.invalidateQueries(keys.policies);
      qc.invalidateQueries(["trust", "audit"]);
    },
  });
};

const post = <T>(url: string, body: unknown) =>
  axios.post<T>(url, body).then(({ data }) => data);

export const useCreateTrustPolicy = () => {
  const qc = useQueryClient();
  return useMutation(
    (cmd: CreateTrustPolicyCommand) =>
      post<TrustPolicyDetailDto>(`${admin}/policies`, cmd),
    { onSuccess: () => qc.invalidateQueries(keys.all) },
  );
};

export const useUpdateTrustPolicy = (policyId: string) => {
  const qc = useQueryClient();
  return useMutation(
    (cmd: UpdateTrustPolicyCommand) =>
      axios
        .put<TrustPolicyDetailDto>(`${admin}/policies/${policyId}`, cmd)
        .then(({ data }) => data),
    {
      onSuccess: (policy) => {
        qc.setQueryData(keys.policy(policyId), policy);
        qc.invalidateQueries(keys.policies);
        qc.invalidateQueries(["trust", "audit"]);
      },
    },
  );
};

/** Start a new draft from the current approved version (e.g. annual review). */
export const useStartTrustPolicyDraft = () =>
  useTrustMutation((policyId: string) =>
    post<TrustPolicyVersionDto>(`${admin}/policies/${policyId}/drafts`, {}),
  );

export const useSaveTrustPolicyDraft = (versionId: string) =>
  useTrustMutation((cmd: SaveTrustPolicyDraftCommand) =>
    axios
      .put<TrustPolicyVersionDto>(`${admin}/versions/${versionId}`, cmd)
      .then(({ data }) => data),
  );

export const useProposeTrustPolicyChanges = (versionId: string) =>
  useTrustMutation((cmd: ProposeTrustPolicyChangesCommand) =>
    post<TrustPolicyVersionDto>(`${admin}/versions/${versionId}/changes`, cmd),
  );

export const useDecideTrustPolicyChange = () =>
  useTrustMutation(
    ({ changeId, ...cmd }: DecideTrustPolicyChangeCommand & { changeId: string }) =>
      post<TrustPolicyVersionDto>(`${admin}/changes/${changeId}/decision`, cmd),
  );

export const useSubmitTrustPolicyVersion = (versionId: string) =>
  useTrustMutation((cmd: SubmitTrustPolicyVersionCommand) =>
    post<TrustPolicyVersionDto>(`${admin}/versions/${versionId}/submit`, cmd),
  );

export const useApproveTrustPolicyVersion = (versionId: string) =>
  useTrustMutation(() =>
    post<TrustPolicyVersionDto>(`${admin}/versions/${versionId}/approve`, {}),
  );

export const useReturnTrustPolicyVersion = (versionId: string) =>
  useTrustMutation((cmd: ReturnTrustPolicyVersionCommand) =>
    post<TrustPolicyVersionDto>(`${admin}/versions/${versionId}/return`, cmd),
  );

// Subprocessors and facts shown on the public overview

const subprocessorsKey = ["trust", "subprocessors"] as const;
const factsKey = ["trust", "facts"] as const;

export const useTrustSubprocessors = () =>
  useQuery(subprocessorsKey, () =>
    axios
      .get<TrustSubprocessorDto[]>(`${admin}/subprocessors`)
      .then(({ data }) => data),
  );

const useSubprocessorMutation = <TArgs>(fn: (args: TArgs) => Promise<unknown>) => {
  const qc = useQueryClient();
  return useMutation(fn, {
    onSuccess: () => {
      qc.invalidateQueries(subprocessorsKey);
      qc.invalidateQueries(["trust", "audit"]);
    },
  });
};

export const useCreateTrustSubprocessor = () =>
  useSubprocessorMutation((cmd: SaveTrustSubprocessorCommand) =>
    post<TrustSubprocessorDto>(`${admin}/subprocessors`, cmd),
  );

export const useUpdateTrustSubprocessor = () =>
  useSubprocessorMutation(
    ({ id, ...cmd }: SaveTrustSubprocessorCommand & { id: string }) =>
      axios
        .put<TrustSubprocessorDto>(`${admin}/subprocessors/${id}`, cmd)
        .then(({ data }) => data),
  );

export const useDeleteTrustSubprocessor = () =>
  useSubprocessorMutation((id: string) =>
    axios.delete(`${admin}/subprocessors/${id}`),
  );

export const useTrustFacts = () =>
  useQuery(factsKey, () =>
    axios.get<TrustFactsDto>(`${admin}/facts`).then(({ data }) => data),
  );

export const useUpdateTrustFacts = () => {
  const qc = useQueryClient();
  return useMutation(
    (cmd: UpdateTrustFactsCommand) =>
      axios.put<TrustFactsDto>(`${admin}/facts`, cmd).then(({ data }) => data),
    {
      onSuccess: (facts) => {
        qc.setQueryData(factsKey, facts);
        qc.invalidateQueries(["trust", "audit"]);
      },
    },
  );
};

// Security FAQ

const faqKey = ["trust", "faq"] as const;

export const useTrustFaq = () =>
  useQuery(faqKey, () =>
    axios.get<TrustFaqEntryDto[]>(`${admin}/faq`).then(({ data }) => data),
  );

const useFaqMutation = <TArgs, TRes>(fn: (args: TArgs) => Promise<TRes>) => {
  const qc = useQueryClient();
  return useMutation(fn, {
    onSuccess: () => {
      qc.invalidateQueries(faqKey);
      qc.invalidateQueries(["trust", "audit"]);
    },
  });
};

export const useCreateTrustFaqEntry = () =>
  useFaqMutation((cmd: SaveTrustFaqEntryCommand) =>
    post<TrustFaqEntryDto>(`${admin}/faq`, cmd),
  );

export const useUpdateTrustFaqEntry = () =>
  useFaqMutation(({ id, ...cmd }: SaveTrustFaqEntryCommand & { id: string }) =>
    axios.put<TrustFaqEntryDto>(`${admin}/faq/${id}`, cmd).then(({ data }) => data),
  );

export const useSubmitTrustFaqEntries = () =>
  useFaqMutation((ids: string[]) =>
    post<TrustFaqBatchResultDto>(`${admin}/faq/submit`, { ids }),
  );

export const useApproveTrustFaqEntries = () =>
  useFaqMutation((ids: string[]) =>
    post<TrustFaqBatchResultDto>(`${admin}/faq/approve`, { ids }),
  );

export const useReturnTrustFaqEntry = () =>
  useFaqMutation(({ id, ...cmd }: ReturnTrustFaqEntryCommand & { id: string }) =>
    post<TrustFaqEntryDto>(`${admin}/faq/${id}/return`, cmd),
  );

export const useUnpublishTrustFaqEntry = () =>
  useFaqMutation((id: string) => post<TrustFaqEntryDto>(`${admin}/faq/${id}/unpublish`, {}));

export const useDeleteTrustFaqEntry = () =>
  useFaqMutation((id: string) => axios.delete(`${admin}/faq/${id}`));

export const useReorderTrustFaq = () =>
  useFaqMutation((ids: string[]) =>
    axios.put<TrustFaqEntryDto[]>(`${admin}/faq/order`, { ids }).then(({ data }) => data),
  );

// Public Trust Centre (no sign-in)

export const getPublicTrustFaq = () =>
  axios
    .get<PublicTrustFaqEntryDto[]>("/api/v2/trust/public/faq")
    .then(({ data }) => data);

export const getPublicTrustOverview = () =>
  axios
    .get<PublicTrustOverviewDto>("/api/v2/trust/public/overview")
    .then(({ data }) => data);

export const getPublicTrustPolicies = () =>
  axios
    .get<PublicTrustPolicyDto[]>("/api/v2/trust/public/policies")
    .then(({ data }) => data);

/** The approved mutual NDA. 404 until one has been approved. */
export const getPublicTrustNda = () =>
  axios
    .get<PublicTrustNdaDto>("/api/v2/trust/public/nda")
    .then(({ data }) => data);

export const getPublicTrustPolicy = (slug: string) =>
  axios
    .get<PublicTrustPolicyDto>(`/api/v2/trust/public/policies/${slug}`)
    .then(({ data }) => data);

// Access requests (admin)

const accessKey = ["trust", "access"] as const;

export const useTrustAccessRequests = (status: TrustAccessRequestStatus | null = null) =>
  useQuery([...accessKey, "requests", status], () =>
    axios
      .get<TrustAccessRequestDto[]>(`${admin}/access-requests`, {
        params: status ? { status } : {},
      })
      .then(({ data }) => data),
  );

export const useTrustAccessGrants = () =>
  useQuery([...accessKey, "grants"], () =>
    axios.get<TrustAccessGrantDto[]>(`${admin}/access-grants`).then(({ data }) => data),
  );

export const useTrustCustomerDomains = () =>
  useQuery([...accessKey, "domains"], () =>
    axios
      .get<TrustCustomerDomainDto[]>(`${admin}/customer-domains`)
      .then(({ data }) => data),
  );

const useAccessMutation = <TArgs, TRes>(fn: (args: TArgs) => Promise<TRes>) => {
  const qc = useQueryClient();
  return useMutation(fn, {
    onSuccess: () => {
      qc.invalidateQueries(accessKey);
      qc.invalidateQueries(["trust", "audit"]);
    },
  });
};

export const useApproveTrustAccessRequest = () =>
  useAccessMutation(
    ({ id, ...cmd }: ApproveTrustAccessRequestCommand & { id: string }) =>
      post<TrustAccessRequestDto>(`${admin}/access-requests/${id}/approve`, cmd),
  );

export const useDeclineTrustAccessRequest = () =>
  useAccessMutation(
    ({ id, ...cmd }: DeclineTrustAccessRequestCommand & { id: string }) =>
      post<TrustAccessRequestDto>(`${admin}/access-requests/${id}/decline`, cmd),
  );

export const useRevokeTrustAccessGrant = () =>
  useAccessMutation((id: string) =>
    post<TrustAccessGrantDto>(`${admin}/access-grants/${id}/revoke`, {}),
  );

export const useCreateTrustCustomerDomain = () =>
  useAccessMutation((cmd: CreateTrustCustomerDomainCommand) =>
    post<TrustCustomerDomainDto>(`${admin}/customer-domains`, cmd),
  );

export const useDeleteTrustCustomerDomain = () =>
  useAccessMutation((id: string) => axios.delete(`${admin}/customer-domains/${id}`));

// Gated access (public site). Send the magic-link token as a header.

const accessHeaders = (token: string) => ({ headers: { "X-Trust-Access": token } });

/** Always resolves the same way whatever the email, so it can't reveal customers. */
export const createTrustAccessRequest = (cmd: CreateTrustAccessRequestCommand) =>
  axios
    .post<{ message: string }>("/api/v2/trust/public/access-requests", cmd)
    .then(({ data }) => data);

export const getTrustAccessSession = (token: string) =>
  axios
    .get<TrustAccessSessionDto>("/api/v2/trust/public/access/me", accessHeaders(token))
    .then(({ data }) => data);

export const getTrustRestrictedDocument = (token: string, slug: string) =>
  axios
    .get<TrustRestrictedDocumentDto>(
      `/api/v2/trust/public/access/policies/${slug}`,
      accessHeaders(token),
    )
    .then(({ data }) => data);

/** The whole FAQ, restricted answers included (each fetch is audited). */
export const getTrustAccessFaq = (token: string) =>
  axios
    .get<PublicTrustFaqEntryDto[]>("/api/v2/trust/public/access/faq", accessHeaders(token))
    .then(({ data }) => data);

/**
 * Any version as a PDF (staff only), watermarked with whether it's in force
 * and who may see it. Each download is in the audit log.
 */
export const getTrustVersionPdf = (versionId: string) =>
  axios
    .get<Blob>(`${admin}/versions/${versionId}/pdf`, { responseType: "blob" })
    .then(({ data }) => data);

/** The watermarked PDF as a Blob, for a download link. */
export const getTrustRestrictedDocumentPdf = (token: string, slug: string) =>
  axios
    .get<Blob>(`/api/v2/trust/public/access/policies/${slug}/pdf`, {
      ...accessHeaders(token),
      responseType: "blob",
    })
    .then(({ data }) => data);

// Asset register (Cyber Essentials): staff devices and cloud services

const assetsKey = ["trust", "assets"] as const;
const assetKey = (id: string) => ["trust", "assets", id] as const;

export const useTrustAssets = () =>
  useQuery(assetsKey, () =>
    axios.get<TrustAssetDto[]>(`${admin}/assets`).then(({ data }) => data),
  );

export const useTrustAsset = (id: string) =>
  useQuery(
    assetKey(id),
    () => axios.get<TrustAssetDetailDto>(`${admin}/assets/${id}`).then(({ data }) => data),
    { enabled: !!id },
  );

const useAssetMutation = <TArgs>(fn: (args: TArgs) => Promise<TrustAssetDto>) => {
  const qc = useQueryClient();
  return useMutation(fn, {
    onSuccess: () => {
      // Covers the list and every detail (assetKey starts with assetsKey).
      qc.invalidateQueries(assetsKey);
      qc.invalidateQueries(["trust", "audit"]);
    },
  });
};

export const useCreateTrustAsset = () =>
  useAssetMutation((cmd: SaveTrustAssetCommand) => post<TrustAssetDto>(`${admin}/assets`, cmd));

export const useUpdateTrustAsset = () =>
  useAssetMutation(({ id, ...cmd }: SaveTrustAssetCommand & { id: string }) =>
    axios.put<TrustAssetDto>(`${admin}/assets/${id}`, cmd).then(({ data }) => data),
  );

export const useConfirmTrustAsset = () =>
  useAssetMutation((id: string) => post<TrustAssetDto>(`${admin}/assets/${id}/confirm`, {}));

export const useSetTrustAssetStatus = () =>
  useAssetMutation(({ id, change }: { id: string; change: SetTrustAssetStatusCommand }) =>
    post<TrustAssetDto>(`${admin}/assets/${id}/status`, change),
  );
